/**
 * Agent 节点配置面板
 *
 * 配置 Agent 选择、模型选择、工具权限、章节范围等
 */

import React, { useState, useCallback, useEffect } from 'react';
import { clsx } from 'clsx';
import type { AgentNodeData, LoopNodeData } from '../../../types/workflow';
import type { Agent, ToolName, ToolCategory } from '../../../types/agent';
import { getAllToolsInfo, READ_TOOLS, WRITE_TOOLS, WEB_TOOLS } from '../../../types/agent';
import { useWorkflowStore } from '../../../store/workflowStore';
import { useAgentStore } from '../../../store/agentStore';
import { useModelStore } from '../../../store/modelStore';
import { useChapterRangeValidation } from '../../../hooks/useChapterRangeValidation';

export interface AgentNodeConfigPanelProps {
  /** 节点 ID */
  nodeId: string;
}

export const AgentNodeConfigPanel: React.FC<AgentNodeConfigPanelProps> = React.memo(({
  nodeId,
}) => {
  // 从 store 获取节点数据和工作区
  const node = useWorkflowStore((state) =>
    state.nodes.find((n) => n.id === nodeId)
  );
  const updateNodeForm = useWorkflowStore((state) => state.updateNodeForm);

  // 从 agentStore 获取智能体列表
  const agents = useAgentStore((state) => state.agents);
  const fetchAgents = useAgentStore((state) => state.fetchAgents);
  const agentStoreLoading = useAgentStore((state) => state.loading);

  // 从 modelStore 获取可用模型
  const availableModels = useModelStore((state) => state.availableModelIds);
  const modelsInfo = useModelStore((state) => state.modelsInfo);
  const fetchModels = useModelStore((state) => state.fetchModels);
  const modelStoreLoading = useModelStore((state) => state.isLoading);

  // 本地表单状态
  const [formData, setFormData] = useState<{
    label: string;
    agent_id: number | null;
    agent_name: string;
    model: string;
    enabled_tools: ToolName[];
  }>({
    label: '',
    agent_id: null,
    agent_name: '',
    model: 'glm-4.6',
    enabled_tools: [],
  });

  // 错误状态
  const [errors, setErrors] = useState<{
    agent_id?: string;
    model?: string;
  }>({});

  // 所有工具信息
  const allTools = getAllToolsInfo();

  // 按 category 分组工具
  const toolsByCategory: Record<ToolCategory, typeof allTools> = {
    read: allTools.filter(t => t.category === 'read'),
    write: allTools.filter(t => t.category === 'write'),
    web: allTools.filter(t => t.category === 'web'),
  };

  // 使用章节范围验证 Hook
  const chapterValidation = useChapterRangeValidation(nodeId);

  // 父级循环容器
  const parentLoopNode = chapterValidation.parentLoopNode;

  // 允许的章节范围
  const allowedRange = chapterValidation.allowedRange;

  // 同步节点数据到本地状态
  useEffect(() => {
    if (node) {
      const data = node.data as AgentNodeData;
      setFormData({
        label: data.label || 'Agent',
        agent_id: data.agent_id,
        agent_name: data.agent_name || '',
        model: data.model || 'glm-4.6',
        enabled_tools: data.enabled_tools || [],
      });
    }
  }, [node]);

  // 加载 Agent 列表
  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  // 加载模型列表
  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  // 验证表单
  const validateForm = useCallback(() => {
    const newErrors: typeof errors = {};

    if (!formData.agent_id) {
      newErrors.agent_id = '请选择一个 Agent';
    }

    if (!formData.model) {
      newErrors.model = '请选择一个模型';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // 保存配置
  const handleSave = useCallback(() => {
    if (!validateForm()) {
      return;
    }

    updateNodeForm(nodeId, {
      label: formData.label,
      agent_id: formData.agent_id,
      agent_name: formData.agent_name,
      model: formData.model,
      enabled_tools: formData.enabled_tools,
    });
  }, [nodeId, formData, updateNodeForm, validateForm]);

  // 更新字段
  const updateField = useCallback(<K extends keyof typeof formData>(
    field: K,
    value: (typeof formData)[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // 清除对应错误
    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as keyof typeof errors];
        return next;
      });
    }
  }, [errors]);

  // 选择 Agent
  const handleAgentSelect = useCallback((agent: Agent) => {
    setFormData({
      ...formData,
      agent_id: agent.id,
      agent_name: agent.name,
      // 默认启用该 Agent 的所有工具
      enabled_tools: agent.enabled_tools || [],
    });
    setErrors((prev) => ({ ...prev, agent_id: undefined }));
  }, [formData]);

  // 选择模型
  const handleModelSelect = useCallback((model: string) => {
    updateField('model', model);
    setErrors((prev) => ({ ...prev, model: undefined }));
  }, [updateField]);

  // 切换工具启用状态
  const toggleTool = useCallback((toolName: ToolName) => {
    setFormData((prev) => {
      const enabledTools = prev.enabled_tools.includes(toolName)
        ? prev.enabled_tools.filter((t) => t !== toolName)
        : [...prev.enabled_tools, toolName];
      return { ...prev, enabled_tools: enabledTools as ToolName[] };
    });
  }, []);

  // 全选工具
  const selectAllTools = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      enabled_tools: [...READ_TOOLS, ...WRITE_TOOLS, ...WEB_TOOLS] as ToolName[],
    }));
  }, []);

  // 反选工具
  const invertTools = useCallback(() => {
    setFormData((prev) => {
      const allToolNames = [...READ_TOOLS, ...WRITE_TOOLS, ...WEB_TOOLS] as ToolName[];
      const newEnabledTools = allToolNames.filter(
        (t) => !prev.enabled_tools.includes(t)
      ) as ToolName[];
      return { ...prev, enabled_tools: newEnabledTools };
    });
  }, []);

  // 清空工具
  const clearTools = useCallback(() => {
    setFormData((prev) => ({ ...prev, enabled_tools: [] }));
  }, []);

  // 获取模型显示名称
  const getModelDisplayName = (modelId: string) => {
    const info = modelsInfo[modelId];
    if (info?.name) return info.name;
    return modelId;
  };

  return (
    <div className="p-4 space-y-4">
      {/* 节点名称 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          节点名称
        </label>
        <input
          type="text"
          value={formData.label}
          onChange={(e) => updateField('label', e.target.value)}
          placeholder="Agent"
          className={clsx(
            'w-full px-3 py-2 rounded-md border border-gray-200',
            'focus:outline-none focus:ring-2 focus:ring-paper-500 focus:border-transparent',
            'text-sm text-gray-700',
          )}
        />
      </div>

      {/* Agent 选择 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          选择 Agent <span className="text-red-500">*</span>
        </label>
        {agentStoreLoading ? (
          <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
            <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            加载中...
          </div>
        ) : agents.length === 0 ? (
          <div className="text-sm text-gray-500 py-2">
            暂无可用 Agent，请先创建 Agent
          </div>
        ) : (
          <div className="space-y-1 max-h-48 overflow-y-auto border border-gray-200 rounded-md">
            {agents.map((agent) => (
              <button
                key={agent.id}
                onClick={() => handleAgentSelect(agent)}
                className={clsx(
                  'w-full px-3 py-2 text-left text-sm transition-colors',
                  'hover:bg-gray-50',
                  formData.agent_id === agent.id
                    ? 'bg-paper-50 text-paper-800 border-l-2 border-paper-500'
                    : 'text-gray-700',
                )}
              >
                <div className="font-medium">{agent.name}</div>
                {agent.prompt && (
                  <div className="text-xs text-gray-400 truncate mt-0.5">
                    {agent.prompt.substring(0, 50)}...
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
        {errors.agent_id && (
          <p className="mt-1 text-sm text-red-500">{errors.agent_id}</p>
        )}
        {formData.agent_name && (
          <p className="mt-1 text-xs text-gray-500">
            已选择: {formData.agent_name}
          </p>
        )}
      </div>

      {/* 模型选择 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          选择模型 <span className="text-red-500">*</span>
        </label>
        {modelStoreLoading ? (
          <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
            <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            加载模型列表...
          </div>
        ) : availableModels.length === 0 ? (
          <div className="text-sm text-gray-500 py-2">
            暂无可用模型，请检查系统配置
          </div>
        ) : (
          <div className="space-y-1 max-h-48 overflow-y-auto border border-gray-200 rounded-md">
            {availableModels.map((modelId) => {
              const modelInfo = modelsInfo[modelId];
              const isSelected = formData.model === modelId;
              return (
                <button
                  key={modelId}
                  onClick={() => handleModelSelect(modelId)}
                  className={clsx(
                    'w-full px-3 py-2 text-left text-sm transition-colors',
                    'hover:bg-gray-50',
                    isSelected
                      ? 'bg-blue-50 text-blue-700 border-l-2 border-blue-500'
                      : 'text-gray-700',
                  )}
                >
                  <div className="font-medium">{getModelDisplayName(modelId)}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {modelInfo?.provider && (
                      <span className="text-xs text-gray-400">{modelInfo.provider}</span>
                    )}
                    {modelInfo?.supports_thinking && (
                      <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                        思考
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {errors.model && (
          <p className="mt-1 text-sm text-red-500">{errors.model}</p>
        )}
        {formData.model && (
          <p className="mt-1 text-xs text-gray-500">
            已选择: {getModelDisplayName(formData.model)}
          </p>
        )}
      </div>

      {/* 章节范围限制（从循环容器继承） */}
      {parentLoopNode && allowedRange && (
        <div className="p-3 bg-purple-50 rounded-md border border-purple-200">
          <div className="flex items-center gap-2 text-sm font-medium text-purple-700 mb-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            允许的章节范围
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-lg font-bold text-purple-600">
              {allowedRange.start} - {allowedRange.end}
            </span>
            <span className="text-xs text-purple-500">
              (共 {allowedRange.end - allowedRange.start + 1} 章)
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            此 Agent 的工具调用章节参数必须在此范围内
          </p>
          <p className="text-xs text-purple-400 mt-1">
            继承自循环容器: {(parentLoopNode.data as LoopNodeData).label || '循环容器'}
          </p>
        </div>
      )}

      {/* 无循环容器时的提示 */}
      {!parentLoopNode && (
        <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            章节范围
          </div>
          <p className="text-xs text-gray-500">
            此 Agent 未在循环容器内，可操作任意章节
          </p>
          <p className="text-xs text-gray-400 mt-1">
            将 Agent 放入循环容器可限制其章节操作范围
          </p>
        </div>
      )}

      {/* 工具权限配置 */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">
            工具权限配置
          </label>
          <div className="flex items-center gap-1">
            <button
              onClick={selectAllTools}
              className="px-2 py-0.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            >
              全选
            </button>
            <button
              onClick={invertTools}
              className="px-2 py-0.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            >
              反选
            </button>
            <button
              onClick={clearTools}
              className="px-2 py-0.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            >
              清空
            </button>
          </div>
        </div>

        {/* 只读工具 */}
        <div className="mb-3">
          <div className="text-xs font-medium text-gray-500 mb-1.5">只读工具</div>
          <div className="space-y-1">
            {toolsByCategory.read.map((tool) => (
              <label
                key={tool.name}
                className={clsx(
                  'flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer',
                  'hover:bg-gray-50 transition-colors',
                )}
              >
                <input
                  type="checkbox"
                  checked={formData.enabled_tools.includes(tool.name)}
                  onChange={() => toggleTool(tool.name)}
                  className="w-4 h-4 rounded border-gray-300 text-paper-600 focus:ring-paper-500"
                />
                <span className="text-sm text-gray-700">{tool.name}</span>
                <span className="text-xs text-gray-400 flex-1 truncate">
                  {tool.description}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* 写入工具 */}
        <div className="mb-3">
          <div className="text-xs font-medium text-gray-500 mb-1.5">写入工具</div>
          <div className="space-y-1">
            {toolsByCategory.write.map((tool) => (
              <label
                key={tool.name}
                className={clsx(
                  'flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer',
                  'hover:bg-gray-50 transition-colors',
                )}
              >
                <input
                  type="checkbox"
                  checked={formData.enabled_tools.includes(tool.name)}
                  onChange={() => toggleTool(tool.name)}
                  className="w-4 h-4 rounded border-gray-300 text-paper-600 focus:ring-paper-500"
                />
                <span className="text-sm text-gray-700">{tool.name}</span>
                <span className="text-xs text-gray-400 flex-1 truncate">
                  {tool.description}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* 网络工具 */}
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1.5">网络工具</div>
          <div className="space-y-1">
            {toolsByCategory.web.map((tool) => (
              <label
                key={tool.name}
                className={clsx(
                  'flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer',
                  'hover:bg-gray-50 transition-colors',
                )}
              >
                <input
                  type="checkbox"
                  checked={formData.enabled_tools.includes(tool.name)}
                  onChange={() => toggleTool(tool.name)}
                  className="w-4 h-4 rounded border-gray-300 text-paper-600 focus:ring-paper-500"
                />
                <span className="text-sm text-gray-700">{tool.name}</span>
                <span className="text-xs text-gray-400 flex-1 truncate">
                  {tool.description}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* 变量引用提示 */}
      <div className="p-3 bg-gray-50 rounded-md">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          可用变量引用
        </div>
        <div className="space-y-1 text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <code className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">{'{workspace.*}'}</code>
            <span>工作区信息</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">{'{loop.iteration_index}'}</code>
            <span>当前迭代索引</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">{'{loop.chapter_start}'}</code>
            <span>循环起始章节</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">{'{info.节点名.字段}'}</code>
            <span>信息节点提取的数据</span>
          </div>
        </div>
      </div>

      {/* 保存按钮 */}
      <button
        onClick={handleSave}
        className={clsx(
          'w-full py-2 px-4 rounded-md text-sm font-medium',
          'bg-ink-800 text-white',
          'hover:bg-ink-700 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-paper-500 focus:ring-offset-2',
        )}
      >
        保存配置
      </button>
    </div>
  );
});

AgentNodeConfigPanel.displayName = 'AgentNodeConfigPanel';

export default AgentNodeConfigPanel;